import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  ShoppingCart as ShoppingCartIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import AdminPage from "./AdminPage";
import { useDashboardOverview, useSalesAnalytics } from "../../hooks/useAnalytics";
import { AnalyticsFilter } from "../../types/analytics";
import PeriodSelector from "../../components/backend/analytics/PeriodSelector";
import KpiStatCard from "../../components/backend/analytics/KpiStatCard";
import RevenueChart from "../../components/backend/analytics/RevenueChart";
import PieBreakdown from "../../components/backend/analytics/PieBreakdown";
import StatusBreakdownChart from "../../components/backend/analytics/StatusBreakdownChart";

const AnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<AnalyticsFilter>({ period: "30d" });

  const { data: overviewResponse, isLoading: overviewLoading } =
    useDashboardOverview(filter);
  const { data: salesResponse, isLoading: salesLoading } =
    useSalesAnalytics(filter);

  const overview = overviewResponse?.data;
  const sales = salesResponse?.data;
  const isLoading = overviewLoading || salesLoading;

  const navCards = [
    {
      title: t("admin.analytics.salesOverview"),
      path: "/admin/analytics/sales",
      icon: <AttachMoneyIcon />,
      color: "#10b981",
    },
    {
      title: t("admin.analytics.customerInsights"),
      path: "/admin/analytics/customers",
      icon: <PeopleIcon />,
      color: "#6366f1",
    },
    {
      title: t("admin.analytics.productPerformance"),
      path: "/admin/analytics/products",
      icon: <ShoppingCartIcon />,
      color: "#f59e0b",
    },
    {
      title: t("admin.analytics.revenueTimeline"),
      path: "/admin/analytics/revenue",
      icon: <TrendingUpIcon />,
      color: "#ec4899",
    },
  ];

  return (
    <AdminPage>
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography variant="h4" fontWeight={700}>
            {t("admin.analytics.title")}
          </Typography>
          <PeriodSelector filter={filter} onChange={setFilter} />
        </Box>

        {/* Navigation Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {navCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.path}>
              <Card
                sx={{
                  cursor: "pointer",
                  transition: "all 0.2s",
                  border: `1px solid ${card.color}20`,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 8px 24px ${card.color}20`,
                  },
                }}
                onClick={() => navigate(card.path)}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ color: card.color }}>{card.icon}</Box>
                    <Typography variant="body1" fontWeight={600}>
                      {card.title}
                    </Typography>
                  </Box>
                  <ArrowForwardIcon
                    sx={{ color: "text.secondary", fontSize: 20 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* KPI Cards */}
            {overview && (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <KpiStatCard
                    title={t("admin.analytics.totalSales")}
                    data={overview.totalSales}
                    prefix="$"
                    icon={<AttachMoneyIcon />}
                    color="#10b981"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <KpiStatCard
                    title={t("admin.analytics.totalOrders")}
                    data={overview.totalOrders}
                    icon={<ShoppingCartIcon />}
                    color="#6366f1"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <KpiStatCard
                    title={t("admin.analytics.totalCustomers")}
                    data={overview.totalCustomers}
                    icon={<PeopleIcon />}
                    color="#f59e0b"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <KpiStatCard
                    title={t("admin.analytics.avgOrderValue")}
                    data={overview.averageOrderValue}
                    prefix="$"
                    icon={<TrendingUpIcon />}
                    color="#ec4899"
                  />
                </Grid>
              </Grid>
            )}

            {/* Charts */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                {sales && (
                  <RevenueChart
                    title={t("admin.analytics.revenueOverTime")}
                    revenueData={sales.revenueTimeline}
                    orderCountData={sales.orderCountTimeline}
                    height={300}
                    showOrderCount
                  />
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                {overview && (
                  <StatusBreakdownChart
                    title={t("admin.analytics.orderStatusBreakdown")}
                    data={overview.orderStatusBreakdown}
                  />
                )}
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </AdminPage>
  );
};

export default AnalyticsPage;
